import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Table, Button, Space, Tag, Modal, Input, message, Typography, Empty,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import * as api from 'util/api';
import SkuPicker from './SkuPicker';

const { Text } = Typography;

const STATUS_COLORS = { draft: 'blue', applied: 'green' };

/** Stable empty Set for SkuPicker when no proposal exists yet (avoid new Set() each render). */
const EMPTY_EXISTING_SKU_KEYS = new Set();

const ProposalsList = ({ onSelect }) => {
  const history = useHistory();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedSkuKeys, setSelectedSkuKeys] = useState([]);
  const [skuSearchTerm, setSkuSearchTerm] = useState('');

  const loadProposals = async () => {
    setLoading(true);
    try {
      const data = await api.listPricingProposals();
      setProposals(data);
    } catch (err) {
      message.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProposals(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const proposal = await api.createPricingProposal(newName.trim());
      if (selectedSkuKeys.length > 0) {
        await api.addProposalItems(proposal.id, selectedSkuKeys);
      }
      message.success('Proposal created');
      setCreateModalVisible(false);
      setNewName('');
      setSelectedSkuKeys([]);
      setSkuSearchTerm('');
      onSelect(proposal.id);
    } catch (err) {
      message.error('Failed to create proposal');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deletePricingProposal(id);
      message.success('Proposal deleted');
      loadProposals();
    } catch (err) {
      message.error('Failed to delete proposal');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button type="link" onClick={() => onSelect(record.id)} style={{ padding: 0 }}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>
          {status === 'applied' && <CheckCircleOutlined style={{ marginRight: 4 }} />}
          {status}
        </Tag>
      ),
    },
    {
      title: 'Items',
      key: 'items',
      width: 80,
      render: (_, record) => record.items?.length || 0,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => (date ? dayjs(date).format('DD MMM YYYY HH:mm') : '-'),
    },
    {
      title: 'Applied',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      width: 160,
      render: (date) => (date ? dayjs(date).format('DD MMM YYYY HH:mm') : '-'),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 180,
      render: (email) => <Text type="secondary">{email || '-'}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => onSelect(record.id)}
          />
          {record.status === 'draft' && (
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                Modal.confirm({
                  title: 'Delete proposal?',
                  content: `This will permanently delete "${record.name}".`,
                  onOk: () => handleDelete(record.id),
                  okText: 'Delete',
                  okButtonProps: { danger: true },
                });
              }}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Pricing Proposals</h2>
        <Space>
          <Button
            icon={<PercentageOutlined />}
            onClick={() => history.push('/dashboard/pricing/guide')}
          >
            Pricing Guide
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            New Proposal
          </Button>
        </Space>
      </div>

      <Table
        dataSource={proposals}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty description="No proposals yet" /> }}
      />

      <Modal
        title="Create Pricing Proposal"
        visible={createModalVisible}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewName('');
          setSelectedSkuKeys([]);
          setSkuSearchTerm('');
        }}
        okText={
          selectedSkuKeys.length
            ? `Create with ${selectedSkuKeys.length} SKU${selectedSkuKeys.length !== 1 ? 's' : ''}`
            : 'Create'
        }
        okButtonProps={{ disabled: !newName.trim() }}
        width={960}
      >
        <Input
          placeholder="e.g. Q2 2026 Core Range Increase"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
          autoFocus
          style={{ marginBottom: 16 }}
        />
        <SkuPicker
          existingSkuKeys={EMPTY_EXISTING_SKU_KEYS}
          selectedKeys={selectedSkuKeys}
          onSelectionChange={setSelectedSkuKeys}
          searchTerm={skuSearchTerm}
          onSearchChange={setSkuSearchTerm}
        />
      </Modal>
    </div>
  );
};

ProposalsList.propTypes = {
  onSelect: PropTypes.func.isRequired,
};

export default ProposalsList;
